import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { PhoneCall, CalendarCheck, Video, PackageCheck, EyeOff, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

const steps = [
  {
    icon: PhoneCall,
    title: "Nos cuentas la cuenta",
    body:
      "Una llamada de 20 minutos: quién es el cliente, qué quiere conseguir y qué presupuesto maneja. Te devolvemos tarifas de partner y plazos por escrito.",
  },
  {
    icon: CalendarCheck,
    title: "Preparamos la grabación",
    body:
      "Nos encargamos del guion, el escaleta y la logística. Tu cliente solo recibe la fecha, la hora y qué tiene que llevar puesto.",
  },
  {
    icon: Video,
    title: "Grabamos",
    body:
      "En nuestro estudio de Madrid (C/ Fernando el Católico 57) o en las instalaciones de tu cliente, en toda la Comunidad de Madrid y hasta unos 70 km alrededor. Multicámara 4K, sonido de estudio y una persona dirigiendo la sesión.",
  },
  {
    icon: PackageCheck,
    title: "Te entregamos con tu marca",
    body:
      "Episodio editado, clips verticales, miniaturas y audio, con tus intros y tu línea gráfica. Lo entregas tú, en tu Drive y con tu nombre.",
  },
];

export default function AgenciesWhiteLabel() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="como-funciona"
      className="section-spacing bg-galvani-bg"
      data-testid="agencies-whitelabel"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-cyan font-body font-semibold mb-4">
            Cómo funciona
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-5">
            Todo el mérito para tu agencia: tú mantienes la relación con tu cliente,{" "}
            <span className="gradient-text">nosotros la producción y el trabajo técnico</span>
          </h2>
          <p className="font-body text-base text-galvani-textLight leading-relaxed">
            No contactamos a tu cliente por nuestra cuenta, no firmamos nada con él y no aparecemos
            en los créditos. Somos tu departamento de producción externo.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-12">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
              className="relative p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-shadow"
              data-testid={`agencies-step-${i + 1}`}
            >
              <span className="absolute top-5 right-6 font-heading font-extrabold text-4xl text-slate-100">
                {i + 1}
              </span>
              <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center mb-4 shadow-md">
                <s.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="relative font-heading font-bold text-lg text-galvani-text mb-2">
                {s.title}
              </h3>
              <p className="relative font-body text-sm text-galvani-textLight leading-relaxed">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="rounded-3xl bg-galvani-text p-7 md:p-10 flex flex-col lg:flex-row lg:items-center gap-6 justify-between"
        >
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <EyeOff className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl text-white mb-2">
                Tu cliente no nos verá nunca
              </h3>
              <p className="font-body text-sm text-slate-300 leading-relaxed">
                Firmamos acuerdo de confidencialidad, trabajamos con tu marca y podemos ir a la
                grabación presentados como parte de tu equipo. Las condiciones económicas y el
                margen los cerramos contigo en la reunión, sin tarifas públicas que tu cliente
                pueda encontrar en Google.
              </p>
            </div>
          </div>
          <a href="#solicitar-propuesta" className="flex-shrink-0">
            <Button
              className="bg-white text-galvani-text hover:bg-slate-100 font-body font-semibold rounded-full h-12 px-7 w-full lg:w-auto"
              data-testid="agencies-whitelabel-cta"
            >
              Hablemos de condiciones
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
